<script setup lang="ts">
const route = useRoute()

const socialLinks = [
  {
    name: 'Instagram',
    icon: 'ri:instagram-fill',
    url: 'https://www.instagram.com/kiriminaja_official'
  },
  {
    name: 'Facebook',
    icon: 'ri:facebook-fill',
    url: 'https://www.facebook.com/kiriminaja'
  },
  {
    name: 'YouTube',
    icon: 'ri:youtube-fill',
    url: 'https://youtube.com/channel/UC4zrFKGY7YProakHFWid8oQ'
  },
  {
    name: 'LinkedIn',
    icon: 'ri:linkedin-box-fill',
    url: 'https://www.linkedin.com/company/kiriminaja'
  },
  {
    name: 'TikTok',
    icon: 'ri:tiktok-fill',
    url: 'https://www.tiktok.com/@kiriminajacom'
  }
]

const additionalLinks = [
  {
    title: 'Syarat & Ketentuan',
    to: '/syarat-ketentuan'
  },
  {
    title: 'Kebijakan Privasi',
    to: '/privacy-policy'
  },
  {
    title: 'Status Sistem',
    to: 'https://status.kiriminaja.com/status/public',
    target: '_blank',
    rel: 'nofollow',
    external: true
  }
]

const formDivider = useTemplateRef('footerDivider')
const compact = ref(false)
let scrollTimeout: ReturnType<typeof setTimeout> | null = null

const checkFooterVisibility = () => {
  if (!formDivider.value) return

  const footerRect = formDivider.value.getBoundingClientRect()
  const windowHeight = window.innerHeight

  // Check if footer is visible in viewport - force compact when footer visible
  const footerVisible = footerRect.top <= windowHeight
  if (footerVisible) {
    compact.value = true
  }
}

const handleScroll = () => {
  // Set to compact while scrolling
  compact.value = true
  checkFooterVisibility()

  // Clear existing timeout
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }

  // Set compact to false after scrolling stops (500ms delay)
  scrollTimeout = setTimeout(() => {
    // Only expand if footer is not visible
    if (!formDivider.value) return

    const footerRect = formDivider.value.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const footerVisible = footerRect.top <= windowHeight

    if (!footerVisible) {
      compact.value = false
    }
  }, 300)
}

onMounted(() => {
  checkFooterVisibility()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (scrollTimeout) {
    clearTimeout(scrollTimeout)
  }
})
</script>
<template>
  <footer
    ref="footerDivider"
    class="pb-4"
    :class="{
      'pt-20': !route.path.startsWith('/promotion'),
      'pt-4': route.path.startsWith('/promotion')
    }"
  >
    <UiContainer>
      <template v-if="!route.path.startsWith('/promotion')">
        <div class="grid lg:grid-cols-5 gap-5 md:gap-12">
          <div class="col-span-4 space-y-4 md:col-span-2 md:w-9/12">
            <div class="mb-3">
              <UiLogoDefault />
            </div>
            <div class="flex items-center gap-2 mt-4">
              <NuxtLink
                v-for="(social, index) in socialLinks"
                :key="index"
                :to="$utm.utmUrl(social.url)"
                rel="nofollow"
                class="inline-flex bg-surface-variant hover:bg-primary-container hover:text-primary rounded-full p-1"
                :aria-label="social.name"
                target="_blank"
              >
                <Icon :name="social.icon" size="18px" />
              </NuxtLink>
            </div>
            <div class="space-y-1">
              <p class="text-sm font-semibold">PT Selalu Siap Solusi</p>
              <p>
                Jl. Palagan Tentara Pelajar No.77, Mudal, Sariharjo, Kec.
                Ngaglik, Kabupaten Sleman, Daerah Istimewa Yogyakarta
              </p>
            </div>
            <div class="space-y-1">
              <p class="text-sm font-medium">Terdaftar di</p>
              <div class="flex gap-2.5 items-center">
                <NuxtLink
                  class="px-3 py-2 relative border border-outline-variant inline-block rounded-xl"
                  :to="$utm.utmUrl('https://pse.komdigi.go.id/pse')"
                  aria-label="PSE Kominfo"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Icon name="ka-icon:komdigi" size="24px" />
                </NuxtLink>
                <NuxtLink
                  :to="
                    $utm.utmUrl(
                      'https://sectigostore.com/ssl-tools/ssl-checker.php'
                    )
                  "
                  aria-label="Sectigo SSL Certified"
                  rel="noopener noreferrer"
                  class="px-3 py-2 relative border border-outline-variant inline-block rounded-xl"
                  target="_blank"
                >
                  <Icon name="ka-icon:sectigo" size="24px" />
                </NuxtLink>
              </div>
            </div>
          </div>
          <FooterNavigation />
        </div>
      </template>
      <div
        class="grid lg:grid-cols-5 gap-x-5 gap-y-6 lg:gap-x-12 mt-6 py-6 border-t border-dashed border-outline-variant"
      >
        <p class="lg:col-span-2 order-last lg:order-first">
          © 2020 - {{ new Date().getFullYear() }} PT Selalu Siap Solusi
        </p>
        <div
          class="lg:col-span-3 grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2 lg:gap-x-12"
        >
          <NuxtLink
            v-for="(item, index) in additionalLinks"
            :key="index"
            :to="item.to || '#'"
            class="block hover:text-primary"
            :external="item.external"
            :target="item.target"
            :rel="item.rel"
          >
            <span>{{ item.title }}</span>
          </NuxtLink>
        </div>
      </div>
    </UiContainer>
  </footer>
  <SupportWidget :compact="compact" />
</template>
